import { useAuth0 } from '@auth0/auth0-react';
import ErrorPage from 'pages/ErrorPage/ErrorPage';
import { useContext, useEffect, useReducer, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Stack,
    FormControl,
    FormControlLabel,
    Switch as MuiSwitch,
    MenuItem,
    Select as MuiSelect,
    InputLabel,
    CircularProgress,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UserContext from "../../Context/UserProvider";
import { useApiService } from "../../services/ApiService/ApiService";

function reducer(state: any, action: any) {
    switch (action.input) {
        case "vehicleRegistration":
            return { ...state, vehicleRegistration: action.value }
        case "firstName":
            return { ...state, firstName: action.value }
        case "lastName":
            return { ...state, lastName: action.value }
        case "address":
            return { ...state, address: action.value }
        case "ParkingType":
            return { ...state, parkingType: action.value }
        case "ExistingChargingFacilities":
            return { ...state, existingChargingFacilities: action.value }
        case "ParkingSize":
            return { ...state, parkingSize: action.value }
        case "PropertyStatus":
            return { ...state, propertyStatus: action.value }
        case "PropertyType":
            return { ...state, propertyType: action.value }
        case "evOvernightChargingRate":
            return { ...state, evOvernightChargingRate: action.value }
        default:
            return state
    }
}

interface UnauthenticatedSurveyPageProps {
    error?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
}

function UnauthenticatedSurveyPage({ error }: UnauthenticatedSurveyPageProps) {
    const { isAuthenticated } = useAuth0();
    const { fetchWithAuth } = useApiService();
    const [toggle, setToggle] = useState(true);
    const [missingInput, setMissingInput] = useState("");
    const navigate = useNavigate();
    const location = useLocation();
    const splitURL = location.pathname.toString().split("/");
    
    const goHome = () => {
        let path = isAuthenticated ? '/plan' : '/';
        navigate(path);
    };

    const initialState = {
        vehicleRegistration: "",
        firstName: "",
        lastName: "",
        address: "",
        parkingType: "",
        existingChargingFacilities: true,
        parkingSize: "",
        propertyStatus: "",
        propertyType: "",
        evOvernightChargingRate: toggle
    };

    const [state, dispatch] = useReducer(reducer, initialState);
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reference, setReference] = useState<any>({});
    const [modalIsOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleClose = () => {
        setIsOpen(false);
    };

    const fetchReference = async () => {
        let bodyData = {
            fleetUuid: splitURL[2],
            surveyUuid: splitURL[3]
        };

        const rootUrl = process.env.NODE_ENV === "production" 
            ? process.env.REACT_APP_BACKEND_URL 
            : window.location.origin;

        try {
            const response = await fetchWithAuth(`/survey/referencedata`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData)
            });

            if (response.status !== 200) {
                setIsError(true);
                return;
            }

            const res_json = await response.json();
            const referenceData = {
                ...res_json.referenceData,
                ParkingSize: res_json.referenceData.ParkingSize.map((e: any) => ({
                    ...e,
                    label: e.displayName,
                    name: 'ParkingSize'
                })),
                PropertyType: res_json.referenceData.PropertyType.map((e: any) => ({
                    ...e,
                    label: e.displayName,
                    name: 'PropertyType'
                })),
                PropertyStatus: res_json.referenceData.PropertyStatus.map((e: any) => ({
                    ...e,
                    label: e.displayName,
                    name: 'PropertyStatus'
                })),
                ParkingType: res_json.referenceData.ParkingType.map((e: any) => ({
                    ...e,
                    label: e.displayName,
                    name: 'ParkingType'
                })),
                ExistingChargingFacility: [
                    { label: 'Yes', value: true, name: 'existingChargingFacility' },
                    { label: 'No', value: false, name: 'existingChargingFacility' }
                ]
            };

            setReference(referenceData);
            setLoading(false);
        } catch (error) {
            setIsError(true);
            console.error('Error fetching reference data:', error);
        }
    };

    const validateForm = () => {
        let isValid = true;
        setMissingInput("");

        if (!state.vehicleRegistration) {
            setMissingInput("Vehicle registration is required");
            isValid = false;
        } else if (!state.firstName) {
            setMissingInput("First name is required");
            isValid = false;
        } else if (!state.lastName) {
            setMissingInput("Last name is required");
            isValid = false;
        } else if (!state.address) {
            setMissingInput("Address is required");
            isValid = false;
        }

        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm() || isSubmitting) return;

        setIsSubmitting(true);
        const bodyData = {
            fleetUuid: splitURL[2],
            survey: state,
            surveyUuid: splitURL[3]
        };

        const rootUrl = process.env.NODE_ENV === "production" 
            ? process.env.REACT_APP_BACKEND_URL 
            : window.location.origin;

        try {
            const response = await fetchWithAuth(`/survey/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData)
            });

            if (response.status === 200) {
                setIsOpen(true);
            } else {
                setIsError(true);
            }
        } catch (error) {
            setIsError(true);
            console.error('Error submitting survey:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({
            input: event.target.name,
            value: event.target.value
        });
    };

    const handleSelectChange = (event: any) => {
        dispatch({
            input: event.target.name,
            value: event.target.value
        });
    };

    useEffect(() => {
        fetchReference();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return <ErrorPage statusCode='505' />;
    }

    return (
        <Container 
            maxWidth={false}
            sx={{ 
                pt: 3,
                width: {
                    xs: '100%',    // Full width on mobile
                    sm: '100%',    // Full width on tablet
                    md: '66.67%',  // 2/3 width on medium screens
                    lg: '33.33%',  // 1/3 width on large screens
                },
                mx: 'auto'
            }}
        >
            <Paper 
                elevation={0} 
                sx={{ 
                    borderRadius: 1,
                    overflow: 'hidden',
                    p: 3
                }}
            >
                <Stack spacing={3}>
                    <Typography variant="h6">
                        User Survey
                    </Typography>

                    <TextField
                        fullWidth
                        label="Vehicle Registration Number"
                        name="vehicleRegistration"
                        value={state.vehicleRegistration}
                        onChange={handleChange}
                        error={missingInput.includes("Vehicle registration")}
                        required
                    />

                    <TextField
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={state.firstName}
                        onChange={handleChange}
                        error={missingInput.includes("First name")}
                        required
                    />

                    <TextField
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={state.lastName}
                        onChange={handleChange}
                        error={missingInput.includes("Last name")}
                        required
                    />

                    <TextField
                        fullWidth
                        label="Address"
                        name="address"
                        value={state.address}
                        onChange={handleChange}
                        error={missingInput.includes("Address")}
                        required
                    />

                    <FormControl fullWidth>
                        <InputLabel>Property Type</InputLabel>
                        <MuiSelect
                            name="PropertyType"
                            value={state.propertyType}
                            onChange={handleSelectChange}
                            label="Property Type"
                        >
                            {reference.PropertyType?.map((option: any) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>Existing Charging Facilities</InputLabel>
                        <MuiSelect
                            name="ExistingChargingFacilities"
                            value={state.existingChargingFacilities}
                            onChange={handleSelectChange}
                            label="Existing Charging Facilities"
                        >
                            {reference.ExistingChargingFacility?.map((option: any) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>Parking Type</InputLabel>
                        <MuiSelect
                            name="ParkingType"
                            value={state.parkingType}
                            onChange={handleSelectChange}
                            label="Parking Type"
                        >
                            {reference.ParkingType?.map((option: any) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>Distance from House/Flat (meters)</InputLabel>
                        <MuiSelect
                            name="ParkingSize"
                            value={state.parkingSize}
                            onChange={handleSelectChange}
                            label="Distance from House/Flat (meters)"
                        >
                            {reference.ParkingSize?.map((option: any) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>Property Status</InputLabel>
                        <MuiSelect
                            name="PropertyStatus"
                            value={state.propertyStatus}
                            onChange={handleSelectChange}
                            label="Property Status"
                        >
                            {reference.PropertyStatus?.map((option: any) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <MuiSwitch
                                checked={toggle}
                                onChange={(e) => {
                                    setToggle(e.target.checked);
                                    dispatch({
                                        input: "evOvernightChargingRate",
                                        value: e.target.checked
                                    });
                                }}
                            />
                        }
                        label="Would you look to take advantage of any overnight EV tariff special rate?"
                    />

                    {missingInput && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {missingInput}
                        </Alert>
                    )}

                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                        >
                            Submit
                        </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={() => dispatch({ type: 'RESET' })}
                        >
                            Reset
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            <Dialog
                open={modalIsOpen}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Thank you
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography align="center" variant="body1" sx={{ my: 2 }}>
                        Your survey response has been submitted successfully.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={goHome}>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default UnauthenticatedSurveyPage;
